import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { COCKPIT_CSS } from '../cockpit/cockpitStyles';
import { CRM_CSS } from './crmStyles';
import { useCockpitDb } from '../cockpit/CockpitDataContext';
import { toast } from '../../hooks/use-toast';
import {
  TEAM_STATUS, STATUS_ORDER, statusOf,
  comptes, compteOf, entitesOf, equipesOf,
  fichesOfTeam, initials, fullName,
  addEntite, renameEntite, removeEntite,
  addEquipe, renameEquipe, removeEquipe, setEquipeStatus,
} from './crmData';

const notify = (msg) => toast({ title: msg });

// ─────────────────────────────────────────────────────────────────────────────
// Périmètre — construire la cartographie d'un compte : entités, équipes,
// statut de chaque équipe, et fiches clients rattachées à une équipe.
// ─────────────────────────────────────────────────────────────────────────────

export default function CrmPerimetre() {
  const { db, update } = useCockpitDb();
  const navigate = useNavigate();
  const { account: accountParam } = useParams();
  const urlAccount = accountParam ? decodeURIComponent(accountParam) : null;

  const list = comptes(db);
  const [sel, setSel] = useState(urlAccount || null);
  const account = sel || urlAccount || (list[0] && list[0].name) || null;
  const compte = compteOf(db, account);

  const [newEnt, setNewEnt] = useState('');
  const [newEq, setNewEq] = useState({});        // { [entité]: valeur du champ }
  const [editing, setEditing] = useState(null);  // { kind, entite, team, value }

  const stats = useMemo(() => {
    const all = entitesOf(compte).flatMap((e) => equipesOf(e));
    const by = (s) => all.filter((eq) => statusOf(eq) === s).length;
    return { total: all.length, actif: by('actif'), dev: by('encours') + by('cible'), non: by('non') };
  }, [compte]);

  if (!db) return null;

  // Aucun compte : le périmètre n'a rien à décrire tant qu'il n'y a pas de compte.
  if (!list.length) {
    return (
      <AppLayout title="CRM · Périmètre">
        <style>{COCKPIT_CSS}</style>
        <style>{CRM_CSS}</style>
        <div className="ckpt">
          <div className="ckpt-empty">
            <div className="ic">🏢</div>
            <div className="t">Aucun compte client</div>
            <div className="d">Le périmètre décrit les entités et les équipes d'un compte. Créez d'abord un compte client.</div>
            <button className="btn btn-primary" onClick={() => navigate('/crm/comptes')}>
              + Compte client
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const startEdit = (kind, entite, team, value) => setEditing({ kind, entite, team, value });
  const commitEdit = () => {
    if (!editing) return;
    const v = (editing.value || '').trim();
    const { kind, entite, team } = editing;
    if (v && v !== (kind === 'ent' ? entite : team)) {
      update((n) => (kind === 'ent'
        ? renameEntite(n, account, entite, v)
        : renameEquipe(n, account, entite, team, v)));
      notify(kind === 'ent' ? 'Entité renommée' : 'Équipe renommée');
    }
    setEditing(null);
  };

  const onAddEntite = () => {
    const v = newEnt.trim();
    if (!v) return;
    if (entitesOf(compte).some((e) => e.name === v)) { notify('Cette entité existe déjà'); return; }
    update((n) => addEntite(n, account, v));
    setNewEnt('');
    notify(`Entité « ${v} » ajoutée`);
  };

  const onAddEquipe = (entite) => {
    const v = (newEq[entite] || '').trim();
    if (!v) return;
    const e = entitesOf(compte).find((x) => x.name === entite);
    if (equipesOf(e).some((eq) => eq.name === v)) { notify('Cette équipe existe déjà'); return; }
    update((n) => addEquipe(n, account, entite, v));
    setNewEq((s) => ({ ...s, [entite]: '' }));
    notify(`Équipe « ${v} » ajoutée`);
  };

  const onDeleteEntite = (name, nbEq, nbFiches) => {
    const detail = [nbEq && `${nbEq} équipe(s)`, nbFiches && `${nbFiches} fiche(s) client`]
      .filter(Boolean).join(' et ');
    const msg = detail
      ? `Supprimer l'entité « ${name} » ?\n\nCela supprimera aussi ${detail}.`
      : `Supprimer l'entité « ${name} » ?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    update((n) => removeEntite(n, account, name));
    notify('Entité supprimée');
  };

  const onDeleteEquipe = (entite, team, nbFiches) => {
    const msg = nbFiches
      ? `Supprimer l'équipe « ${team} » ?\n\nCela supprimera aussi ${nbFiches} fiche(s) client.`
      : `Supprimer l'équipe « ${team} » ?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    update((n) => removeEquipe(n, account, entite, team));
    notify('Équipe supprimée');
  };

  return (
    <AppLayout title="CRM · Périmètre">
      <style>{COCKPIT_CSS}</style>
      <style>{CRM_CSS}</style>
      <div className="ckpt">

      {/* Sélecteur de compte */}
      <div className="crm-accbar">
        <span className="crm-accbar-lbl">Compte</span>
        <select
          className="crm-select"
          value={account || ''}
          onChange={(e) => { setSel(e.target.value); navigate(`/crm/perimetre/${encodeURIComponent(e.target.value)}`); }}
        >
          {list.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <span className="sp" />
        <button className="btn btn-ghost" onClick={() => navigate(`/crm/comptes/${encodeURIComponent(account)}`)}>
          Ouvrir la fiche compte →
        </button>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="lbl">Équipes cartographiées</div>
          <div className="val">{stats.total}</div>
          <div className="chip">dans {entitesOf(compte).length} entité(s)</div>
        </div>
        <div className="kpi">
          <div className="lbl">Actives</div>
          <div className="val good">{stats.actif}</div>
          <div className="chip">missions en cours</div>
        </div>
        <div className="kpi">
          <div className="lbl">En cours / cibles</div>
          <div className="val warn">{stats.dev}</div>
          <div className="chip">à développer</div>
        </div>
        <div className="kpi">
          <div className="lbl">Non adressées</div>
          <div className="val">{stats.non}</div>
          <div className="chip">potentiel</div>
        </div>
      </div>

      {/* Ajout d'une entité */}
      <div className="crm-addrow">
        <input
          className="crm-input"
          placeholder="Nom d'une entité (ex. BNP CIB)"
          value={newEnt}
          onChange={(e) => setNewEnt(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onAddEntite()}
        />
        <button className="btn btn-primary" onClick={onAddEntite}>+ Entité</button>
      </div>

      {!entitesOf(compte).length && (
        <div className="ckpt-empty">
          <div className="ic">🗺️</div>
          <div className="t">Périmètre vide</div>
          <div className="d">Ajoutez une entité (ex. « BNP CIB »), puis ses équipes (ex. « Cash Management »). Vous pourrez ensuite y référencer vos contacts clients.</div>
        </div>
      )}

      {entitesOf(compte).map((e) => {
        const eqs = equipesOf(e);
        const nbFichesEnt = eqs.reduce((s, eq) => s + fichesOfTeam(db, account, e.name, eq.name).length, 0);
        const isEditEnt = editing && editing.kind === 'ent' && editing.entite === e.name;
        return (
          <div className="crm-ent" key={e.name}>
            <div className="crm-ent-h">
              <span className="crm-ent-ic">🏢</span>
              {isEditEnt ? (
                <input
                  className="crm-input crm-input-inline"
                  autoFocus
                  value={editing.value}
                  onChange={(ev) => setEditing((s) => ({ ...s, value: ev.target.value }))}
                  onBlur={commitEdit}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') commitEdit(); if (ev.key === 'Escape') setEditing(null); }}
                />
              ) : (
                <button className="crm-ent-name" onClick={() => startEdit('ent', e.name, null, e.name)} title="Renommer">
                  {e.name}
                </button>
              )}
              <span className="crm-ent-meta">{eqs.length} équipe(s) · {nbFichesEnt} fiche(s)</span>
              <span className="sp" />
              <button
                className="btn btn-ghost btn-danger"
                onClick={() => onDeleteEntite(e.name, eqs.length, nbFichesEnt)}
              >
                Supprimer
              </button>
            </div>

            {eqs.map((eq) => {
              const fiches = fichesOfTeam(db, account, e.name, eq.name);
              const st = statusOf(eq);
              const isEditEq = editing && editing.kind === 'eq' && editing.entite === e.name && editing.team === eq.name;
              return (
                <div className="crm-team" key={eq.name}>
                  <div className="crm-team-h">
                    <span className="crm-pill" style={{ background: `${TEAM_STATUS[st].color}22`, color: TEAM_STATUS[st].color }}>
                      {TEAM_STATUS[st].label}
                    </span>
                    {isEditEq ? (
                      <input
                        className="crm-input crm-input-inline"
                        autoFocus
                        value={editing.value}
                        onChange={(ev) => setEditing((s) => ({ ...s, value: ev.target.value }))}
                        onBlur={commitEdit}
                        onKeyDown={(ev) => { if (ev.key === 'Enter') commitEdit(); if (ev.key === 'Escape') setEditing(null); }}
                      />
                    ) : (
                      <button className="crm-team-name" onClick={() => startEdit('eq', e.name, eq.name, eq.name)} title="Renommer">
                        {eq.name}
                      </button>
                    )}
                    <select
                      className="crm-select crm-select-sm"
                      value={st}
                      onChange={(ev) => { update((n) => setEquipeStatus(n, account, e.name, eq.name, ev.target.value)); }}
                    >
                      {STATUS_ORDER.map((k) => <option key={k} value={k}>{TEAM_STATUS[k].label}</option>)}
                    </select>
                    <span className="sp" />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/crm/fiches/new?account=${encodeURIComponent(account)}&entity=${encodeURIComponent(e.name)}&team=${encodeURIComponent(eq.name)}`)}
                    >
                      + Fiche client
                    </button>
                    <button className="btn btn-ghost btn-danger btn-sm" onClick={() => onDeleteEquipe(e.name, eq.name, fiches.length)}>
                      ✕
                    </button>
                  </div>

                  {/* Clients référencés au sein de l'équipe */}
                  {fiches.length ? (
                    <div className="crm-fiches">
                      {fiches.map((f) => (
                        <button className="crm-fiche-chip" key={f.id} onClick={() => navigate(`/crm/fiches/${f.id}`)}>
                          <span className="crm-av">{initials(fullName(f))}</span>
                          <span className="crm-fiche-nm">{fullName(f)}</span>
                          {f.fonction && <span className="crm-fiche-fn">{f.fonction}</span>}
                          {f.decisionnaire && <span className="crm-deci" title="Décisionnaire">★</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="crm-team-empty">Aucun client référencé dans cette équipe.</div>
                  )}
                </div>
              );
            })}

            {/* Ajout d'une équipe dans cette entité */}
            <div className="crm-addrow crm-addrow-sub">
              <input
                className="crm-input"
                placeholder="Nom d'une équipe (ex. Cash Management)"
                value={newEq[e.name] || ''}
                onChange={(ev) => setNewEq((s) => ({ ...s, [e.name]: ev.target.value }))}
                onKeyDown={(ev) => ev.key === 'Enter' && onAddEquipe(e.name)}
              />
              <button className="btn btn-ghost" onClick={() => onAddEquipe(e.name)}>+ Équipe</button>
            </div>
          </div>
        );
      })}
      </div>
    </AppLayout>
  );
}
